import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { saveCartToDb, loadCartFromDb, clearCartInDb } from '../services/userService';
import { sanitizeCart, mergeCarts, assertNoDuplicates } from '../utils/cartUtils';
import { logCartEvent } from '../utils/cartLogger';

const CART_STORAGE_KEY = 'panch_tatva_cart';
const DB_SYNC_DELAY_MS = 1500; // debounce: 1.5s after last cart change before syncing to DB
const MAX_ITEM_QUANTITY = 20;  // upper sanity bound per item
const MAX_CART_SIZE     = 50;  // Part 4: alert if cart grows beyond this
const FALLBACK_THRESHOLD = 3; // Part 5: consecutive failures before entering fallback mode
const CONFLICT_ALERT_THRESHOLD = 3; // Part 2: log high_conflict_rate after this many 409s

// Create Context
const CartContext = createContext();

// Custom hook for easy access
export const useCart = () => useContext(CartContext);

// Provider component
export const CartProvider = ({ children }) => {
  const { isAuthenticated, getIdToken } = useAuth();
  const dbSyncTimer = useRef(null);

  // Version tracking for optimistic concurrency (PUT /api/cart with version)
  const cartVersionRef = useRef(null);

  // Pre-merge local snapshot — passed to saveCartToDb for 409 retry so quantities
  // are never double-counted (Issue 1 fix from Feature 22)
  const originalLocalRef = useRef([]);

  // Storage event guard — prevents storage→sync→storage infinite loop (Issue 2, Feature 22)
  const isExternalUpdateRef = useRef(false);

  // Part 2: track 409 conflicts in this session to detect high conflict rates
  const conflictCountRef = useRef(0);

  // Part 5: track consecutive API failures; enter fallback mode after threshold
  const failureCountRef = useRef(0);
  const fallbackModeRef = useRef(false); // true = stop auto-sync, keep local only

  // Initialise from localStorage — sanitizeCart ensures no corrupt items leak in
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return sanitizeCart(saved ? JSON.parse(saved) : []);
    } catch {
      return [];
    }
  });

  // ── localStorage sync ──────────────────────────────────────────────────────
  // Skip DB sync when the state change originated from another tab (Issue 2 fix)
  useEffect(() => {
    if (isExternalUpdateRef.current) {
      isExternalUpdateRef.current = false;
      try { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems)); } catch {}
      return;
    }
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch {
      // localStorage unavailable (private browsing quota)
    }
  }, [cartItems]);

  // ── Multi-tab sync ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key !== CART_STORAGE_KEY) return;
      try {
        const updated = sanitizeCart(e.newValue ? JSON.parse(e.newValue) : []);
        isExternalUpdateRef.current = true; // mark before state update (Issue 2 fix)
        setCartItems(updated);
      } catch {
        // malformed storage event — ignore
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // ── MongoDB sync (debounced) ───────────────────────────────────────────────
  // The 1500ms setTimeout IS the debounce — rapid mutations collapse into one PUT.
  const scheduleDbSync = useCallback((items) => {
    if (!isAuthenticated) return;

    // Part 5: fallback mode — stop auto-syncing after repeated failures
    if (fallbackModeRef.current) return;

    if (dbSyncTimer.current) clearTimeout(dbSyncTimer.current);
    dbSyncTimer.current = setTimeout(async () => {
      try {
        const idToken = await getIdToken();
        if (!idToken) return;

        // Pre-save canonicalization (Feature 21, Part 2)
        const canonical = mergeCarts([], sanitizeCart(items));

        // Part 4: cart size guard
        if (canonical.length > MAX_CART_SIZE) {
          logCartEvent('cart_size_exceeded', { size: canonical.length });
        }

        const result = await saveCartToDb(
          idToken,
          canonical,
          cartVersionRef.current,
          originalLocalRef.current.length > 0 ? originalLocalRef.current : null,
        );

        // Always update version from server response (success + retry paths)
        if (result?.version != null) {
          cartVersionRef.current = result.version;
        }

        // If a 409 was auto-resolved, reconcile state with the authoritative merge
        if (result?.mergedItems) {
          // Part 2: increment conflict counter
          conflictCountRef.current += 1;
          logCartEvent('cart_conflict_resolved', {
            version: result.version ?? null,
            count: conflictCountRef.current,
          });

          if (conflictCountRef.current >= CONFLICT_ALERT_THRESHOLD) {
            logCartEvent('high_conflict_rate', { count: conflictCountRef.current });
          }

          setCartItems(result.mergedItems);
        }

        logCartEvent('cart_saved', {
          itemCount: canonical.length,
          version: result?.version ?? null,
        });

        // Reset failure counter on any success
        failureCountRef.current = 0;
      } catch (err) {
        // CartRateLimitError (429): do NOT count as a failure — it's a transient server
        // rate limit. The caller will see a toast; we just log and bail.
        if (err.name === 'CartRateLimitError') {
          logCartEvent('api_failure', { status: 429, error: err.message, context: 'scheduleDbSync' });
          return;
        }

        // CartServiceUnavailableError (503): backend is down — enter fallback mode immediately
        // rather than waiting for FALLBACK_THRESHOLD consecutive failures.
        if (err.name === 'CartServiceUnavailableError') {
          failureCountRef.current = FALLBACK_THRESHOLD;
          if (!fallbackModeRef.current) {
            fallbackModeRef.current = true;
            logCartEvent('fallback_mode_enabled', { status: 503, error: err.message });
          }
          return;
        }

        failureCountRef.current += 1;
        logCartEvent('api_failure', {
          error: err.message,
          failureCount: failureCountRef.current,
        });

        // Part 5: enter fallback mode after threshold consecutive failures
        if (failureCountRef.current >= FALLBACK_THRESHOLD && !fallbackModeRef.current) {
          fallbackModeRef.current = true;
          logCartEvent('fallback_mode_enabled', { failureCount: failureCountRef.current });
        }
      }
    }, DB_SYNC_DELAY_MS);
  }, [isAuthenticated, getIdToken]);

  // ── Load cart from MongoDB on login ───────────────────────────────────────
  const dbLoadAttemptedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      dbLoadAttemptedRef.current = false;
      cartVersionRef.current = null;
      originalLocalRef.current = [];
      conflictCountRef.current = 0;
      failureCountRef.current = 0;
      fallbackModeRef.current = false;
      return;
    }
    if (dbLoadAttemptedRef.current) return;

    (async () => {
      try {
        dbLoadAttemptedRef.current = true;
        const idToken = await getIdToken();
        if (!idToken) return;

        const { items: rawDbItems, version } = await loadCartFromDb(idToken);
        cartVersionRef.current = version;

        logCartEvent('cart_loaded', { itemCount: rawDbItems.length, version });

        if (!rawDbItems || rawDbItems.length === 0) return;

        // Trust-but-verify: canonicalize server data before merging
        const cleanDbItems = mergeCarts([], sanitizeCart(rawDbItems));

        setCartItems((localItems) => {
          // Capture pre-merge snapshot for 409 retry safety (Issue 1 fix, Feature 22)
          originalLocalRef.current = sanitizeCart(localItems);

          const merged = mergeCarts(localItems, cleanDbItems);

          assertNoDuplicates(merged, 'post-login merge');

          // Part 3: merge anomaly detection
          // (Map-based merge guarantees merged ≤ local+db, but guard anyway)
          if (merged.length > localItems.length + cleanDbItems.length) {
            logCartEvent('merge_anomaly', {
              localCount: localItems.length,
              dbCount: cleanDbItems.length,
              mergedCount: merged.length,
            });
          }

          logCartEvent('cart_merged', {
            localCount: localItems.length,
            dbCount: cleanDbItems.length,
            mergedCount: merged.length,
          });

          // Re-save merged state to DB — pass original snapshot for retry safety
          if (idToken) {
            saveCartToDb(idToken, merged, cartVersionRef.current, originalLocalRef.current)
              .then((result) => {
                if (result?.version != null) cartVersionRef.current = result.version;
              })
              .catch((err) => {
                if (err.name === 'CartRateLimitError') {
                  logCartEvent('api_failure', { status: 429, error: err.message, context: 'post-login-resync' });
                } else {
                  logCartEvent('api_failure', { error: err.message, context: 'post-login-resync' });
                }
              });
          }

          return merged;
        });
      } catch (err) {
        logCartEvent('api_failure', { error: err.message, context: 'cart_load' });
      }
    })();
  }, [isAuthenticated, getIdToken]);

  // ── Cart mutations ─────────────────────────────────────────────────────────
  const addToCart = (product) => {
    // Coerce id to string — products.js stores numeric ids (1, 2, 3…) but
    // validateCartItem requires typeof id === 'string'. All state and DB operations
    // from this point on will always carry a string id.
    const normalizedProduct = product?.id != null
      ? { ...product, id: String(product.id) }
      : product;

    if (!normalizedProduct?.id) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[CartContext] addToCart: product has no id — ignored.', product);
      }
      return;
    }
    setCartItems((prev) => {
      const existing = prev.find((item) => item.id === normalizedProduct.id);
      let next;
      if (existing) {
        next = prev.map((item) => {
          if (item.id !== normalizedProduct.id) return item;
          const newQty = (item.quantity || 1) + (normalizedProduct.quantity || 1);
          if (newQty > MAX_ITEM_QUANTITY) {
            logCartEvent('cart_size_exceeded', {
              context: 'quantity_clamp',
              id: item.id,
              requested: newQty,
              clamped: MAX_ITEM_QUANTITY,
            });
          }
          return { ...item, quantity: Math.min(newQty, MAX_ITEM_QUANTITY) };
        });
      } else {
        const clampedQty = Math.min(normalizedProduct.quantity || 1, MAX_ITEM_QUANTITY);
        next = [...prev, { ...normalizedProduct, quantity: clampedQty }];
      }
      assertNoDuplicates(next, 'addToCart');
      scheduleDbSync(next);
      return next;
    });
  };

  const removeFromCart = (id) => {
    const strId = String(id); // coerce for callers passing numeric ids
    setCartItems((prev) => {
      const next = prev.filter((item) => item.id !== strId);
      scheduleDbSync(next);
      return next;
    });
  };

  const updateQuantity = (id, qty) => {
    const strId = String(id); // coerce for callers passing numeric ids
    if (qty <= 0) {
      removeFromCart(strId);
    } else {
      setCartItems((prev) => {
        const clamped = Math.min(qty, MAX_ITEM_QUANTITY);
        if (clamped !== qty) {
          logCartEvent('cart_size_exceeded', {
            context: 'quantity_clamp',
            id: strId,
            requested: qty,
            clamped: MAX_ITEM_QUANTITY,
          });
        }
        const next = prev.map((item) => (item.id === strId ? { ...item, quantity: clamped } : item));
        scheduleDbSync(next);
        return next;
      });
    }
  };

  // Called after order is placed — clears localStorage and calls DELETE /api/cart
  const clearCart = useCallback(async () => {
    if (dbSyncTimer.current) clearTimeout(dbSyncTimer.current);
    setCartItems([]);
    cartVersionRef.current = null;
    originalLocalRef.current = [];
    conflictCountRef.current = 0;
    failureCountRef.current = 0;
    fallbackModeRef.current = false;
    try { localStorage.removeItem(CART_STORAGE_KEY); } catch {}
    if (isAuthenticated) {
      const idToken = await getIdToken();
      if (idToken) await clearCartInDb(idToken);
    }
  }, [isAuthenticated, getIdToken]);

  // Expose only sanitized items — defensive filter before any consumer sees the data
  const safeCartItems = sanitizeCart(cartItems);

  const totalPrice = safeCartItems.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
    0
  );

  // Part 7: dev-only strict mode — freeze the exposed array to catch accidental
  // mutations of cart state from consumer components early.
  if (process.env.NODE_ENV === 'development') {
    Object.freeze(safeCartItems);
  }

  return (
    <CartContext.Provider
      value={{
        cart: safeCartItems,
        cartItems: safeCartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalPrice,
        getTotal: () => totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
